import client from "./client";
import type {
  SelfieExceptionReviewConflict,
  SelfieExceptionReviewResult,
  SelfieVerificationExceptionsResponse,
} from "../interfaces/SelfieEnrollment";

function extractMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { error?: string; message?: string } } }).response;
    if (response?.data?.error) return response.data.error;
    if (response?.data?.message) return response.data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function getSelfieVerificationExceptionsErrorMessage(err: unknown): string {
  return extractMessage(err, "An error occurred while loading selfie exceptions.");
}

/** Message extractor for the review PATCH, mirroring the list one. */
export function reviewSelfieExceptionErrorMessage(err: unknown): string {
  return extractMessage(err, "An error occurred while saving the review.");
}

/**
 * A 409 means somebody else reviewed the row first. The body carries their
 * state, so callers patch the row into the truth rather than showing an error.
 * Returns null for any other failure.
 */
export function getReviewConflict(err: unknown): SelfieExceptionReviewConflict | null {
  if (!err || typeof err !== "object" || !("response" in err)) return null;
  const response = (err as { response?: { status?: number; data?: SelfieExceptionReviewConflict } })
    .response;
  if (response?.status !== 409 || !response.data) return null;
  const data = response.data;
  return typeof data.exceptionID === "number" ? data : null;
}

/** GET /admin/selfieVerificationExceptions?payPeriodID= — one pay period, with counts. */
export async function getSelfieVerificationExceptions(
  payPeriodID: number
): Promise<SelfieVerificationExceptionsResponse> {
  const response = await client.get<SelfieVerificationExceptionsResponse>(
    "admin/selfieVerificationExceptions",
    { params: { payPeriodID } }
  );
  return response.data;
}

/**
 * PATCH /admin/selfieVerificationExceptions/:id/review — mark an exception
 * reviewed, optionally with a note. One-way: there is no un-review, and a second
 * attempt rejects with a 409 (see getReviewConflict).
 */
export async function reviewSelfieException(
  selfieVerificationExceptionID: number,
  reviewNote?: string | null
): Promise<SelfieExceptionReviewResult> {
  const response = await client.patch<SelfieExceptionReviewResult>(
    `admin/selfieVerificationExceptions/${selfieVerificationExceptionID}/review`,
    { reviewNote: reviewNote ?? null }
  );
  return response.data;
}
